import React from "react";
import PropTypes from "prop-types";
import { Table } from "reactstrap";
import { AsyncBoundary } from "../StatusStates";
import { Money, useRbac } from "../RbacGuard";

/**
 * 共用資料表（主計劃：報表複用模板；所有列表頁共用）。
 *
 * 整合 AsyncBoundary（載入/錯誤/查無）＋ RBAC 金額遮罩 ＋ 排序 ＋ 列動作。
 * 報表 29 張、每個 CRUD 列表只需傳 columns/rows，不各自刻表格。
 *
 * 分頁頁尾**不**在這裡渲染 —— 設計稿把 footer 放在白卡之外、坐在頁面底色上
 * (工作項目 2128:22990、訊息查詢 2128:29130;全站統一)。請在 `</Card>` 之後放
 * `<ListFooter page pageSize total onPageChange>`(見 `Pager`)。
 *
 *   <Card><CardBody>
 *     <DataTable columns={…} rows={…} … />
 *   </CardBody></Card>
 *   <ListFooter page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
 */
const DataTable = ({
  columns, rows, rowKey = "id",
  loading, error, onRetry,
  // page/pageSize/total/onPageChange 仍接受(呼叫端常跟 ListFooter 共用同一組),
  // 但本元件不再畫頁尾 —— 避免又被塞回白卡裡。見 PropTypes。
  sort, order, onSort,
  actions, emptyText = "查無資料", rowClassName,
  // 動作欄表頭文字;部分設計稿(如 2128:24455 無法刪除彈窗)刻意留白 → 傳 ""
  actionsLabel = "操作",
  expandedRowKeys, renderExpanded,
}) => {
  const { canSeeAmount } = useRbac();

  const renderCell = (col, row) => {
    if (col.render) return col.render(row);
    if (col.money) return <Money value={row[col.key]} />;
    return row[col.key];
  };

  const handleHeaderClick = (col) => {
    if (!col.sortable || !onSort) return;
    const nextOrder = sort === col.key && order === "asc" ? "desc" : "asc";
    onSort(col.key, nextOrder);
  };

  return (
    <AsyncBoundary
      loading={loading}
      error={error}
      isEmpty={!rows || rows.length === 0}
      onRetry={onRetry}
      skeleton="table"
      empty={<div className="text-center text-muted py-5">{emptyText}</div>}
    >
      <div className="table-responsive">
        <Table hover className="align-middle mb-0">
          <thead className="table-light">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={col.align ? `text-${col.align}` : ""}
                  style={col.sortable ? { cursor: "pointer", userSelect: "none" } : undefined}
                  onClick={() => handleHeaderClick(col)}
                >
                  {col.money && !canSeeAmount ? `${col.label}` : col.label}
                  {col.sortable && sort === col.key ? (
                    <i className={`mdi mdi-chevron-${order === "asc" ? "up" : "down"}`} />
                  ) : null}
                </th>
              ))}
              {actions ? <th className="text-end">{actionsLabel}</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              // 展開列(Figma DCK-05 軟體 2128:25038):子內容佔滿整列,
              // 不切欄 —— 設計稿的子列是縮排的自由排版,不是對齊母表格的欄位
              const isExpanded =
                !!renderExpanded && (expandedRowKeys || []).some((k) => String(k) === String(row[rowKey]));
              return (
                <React.Fragment key={row[rowKey]}>
                  <tr className={typeof rowClassName === "function" ? rowClassName(row) : rowClassName}>
                    {columns.map((col) => (
                      <td key={col.key} className={col.align ? `text-${col.align}` : ""}>
                        {renderCell(col, row)}
                      </td>
                    ))}
                    {actions ? <td className="text-end">{actions(row)}</td> : null}
                  </tr>
                  {isExpanded ? (
                    <tr className="dck-row-expanded">
                      <td colSpan={columns.length + (actions ? 1 : 0)}>{renderExpanded(row)}</td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>
    </AsyncBoundary>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func,
      money: PropTypes.bool,
      align: PropTypes.string,
      sortable: PropTypes.bool,
    })
  ).isRequired,
  rows: PropTypes.array,
  rowKey: PropTypes.string,
  // 逐列樣式:回傳 class(例:審核中的列上顏色註記底色)
  rowClassName: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /** 展開列:目前展開哪幾列(對應 rowKey 的值);沒給就沒有展開功能 */
  expandedRowKeys: PropTypes.array,
  /** 展開列的內容 (row) => node;子內容佔滿整列,不切欄 */
  renderExpanded: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.object]),
  onRetry: PropTypes.func,
  // 仍接受(與 ListFooter 共用同一組 state);本元件不渲染頁尾
  page: PropTypes.number,
  pageSize: PropTypes.number,
  total: PropTypes.number,
  onPageChange: PropTypes.func,
  sort: PropTypes.string,
  order: PropTypes.string,
  onSort: PropTypes.func,
  actions: PropTypes.func,
  emptyText: PropTypes.string,
  actionsLabel: PropTypes.string,
};

export default DataTable;
